import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

const TooltipContent = ({ intro, items, ordered }) => (
  <Box>
    <Typography variant="body2" sx={{ mb: 1 }}>
      {intro}
    </Typography>
    <Box
      component={ordered ? "ol" : "ul"}
      sx={{
        pl: 3,
        m: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {items.map((item) => (
        <Typography key={item.label} component="li" variant="body2">
          <strong>{item.label}:</strong> {item.text}
        </Typography>
      ))}
    </Box>
  </Box>
);

TooltipContent.propTypes = {
  intro: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ).isRequired,
  ordered: PropTypes.bool,
};

TooltipContent.defaultProps = {
  ordered: false,
};

const vehicleStatusFilter = {
  vehicleSource: {
    title: "Vehicle Status",
    options: [
      { label: "Pipeline", value: "pipeline" },
      { label: "In stock", value: "in-stock" },
      { label: "In transit", value: "in-transit" },
      { label: "Retail order", value: "retail-order" },
    ],
    hasInfo: true,
    infoTitle: "Vehicle Source",
    infoText: (
      <TooltipContent
        intro="Location of the vehicle when the record was created"
        ordered
        items={[
          {
            label: "Pipeline",
            text: "Vehicle delivery ETA to dealership is 31 days or more",
          },
          {
            label: "In Stock",
            text: "Vehicle on dealership lot",
          },
          {
            label: "In Transit",
            text: "Vehicle delivery ETA to dealership is 10 days or sooner",
          },
          {
            label: "Retail order",
            text: "Customer entered vehicle specs via Ford.com; No VIN available",
          },
        ]}
      />
    ),
  },
};
// TODO: **EMP-1643 Hide Vehicle Status filter until new story to enable it**
const showVehicleStatusFilter = false;

export const activeFilters = {
  actionRequired: {
    title: "Action required?",
    options: [{ label: "Show only action required", value: "yes" }],
  },
  orderRequestType: {
    title: "Order Request Type",
    permission: "preorderRequestTypeFilter",
    options: [
      { label: "Purchase Request", value: "purchase-request" },
      { label: "Preorder", value: "pre-order" },
    ],
    hasInfo: true,
    infoTitle: "Order Request Type",
    infoText: (
      <TooltipContent
        intro="Type of Digital Retail Record"
        items={[
          {
            label: "Purchase Request",
            text: "Customer submitted orders via Ford.com",
          },
          {
            label: "Preorder",
            text: "Customer submitted leads for UEV",
          },
        ]}
      />
    ),
  },
  ...(showVehicleStatusFilter
    ? { vehicleSource: vehicleStatusFilter.vehicleSource }
    : {}),
  orderStatus: {
    title: "Order Status",
    options: [
      { label: "Created", value: "created" },
      { label: "Accepted", value: "accepted" },
      { label: "Canceled", value: "canceled" },
    ],
    hasInfo: true,
    infoTitle: "Order Status",
    infoText: (
      <TooltipContent
        intro="Current eComm Order status based on action within EMP"
        ordered
        items={[
          {
            label: "Accepted",
            text: "Record has been accepted by the dealer in EMP",
          },
          {
            label: "Created",
            text: "Record has been submitted by customer but not actioned on by dealer",
          },
          {
            label: "Canceled",
            text: "Record has been canceled by dealer or customer",
          },
        ]}
      />
    ),
  },
};

const tooltipBody = (description, items, ordered = true) => (
  <>
    <span>{description}</span>
    {React.createElement(
      ordered ? "ol" : "ul",
      { style: { paddingLeft: 18, marginBottom: 0 } },
      items.map(([label, text]) => (
        <li key={label}>
          <strong>{label}:</strong> {text}
        </li>
      ))
    )}
  </>
);

export const lincolnFilters = {
  actionRequired: {
    title: "Action required?",
    options: [{ label: "Show only action required", value: "yes" }],
  },
  orderStatus: {
    title: "Order Status",
    options: [
      { label: "Created", value: "created" },
      { label: "Accepted", value: "accepted" },
      { label: "Canceled", value: "canceled" },
    ],
    hasInfo: true,
  },
};

export const isLincolnClientHandlingUser = (user) => {
  const roles = Array.isArray(user?.roles)
    ? user.roles
    : user?.roles && typeof user.roles === "object"
      ? Object.values(user.roles)
      : [];

  return (
    user?.brand === "L" &&
    ["EMP_ECOMM_CUSTOMERHANDLING", "EMP_MODELE"].some((role) =>
      roles.includes(role)
    )
  );
};

export const getActiveFilters = (
  brand,
  market,
  canadianPreorder = false,
  t = (key) => key,
  hideLincolnActionRequired = false
) => {
  if (brand === "L") {
    return {
      ...(hideLincolnActionRequired
        ? { orderStatus: lincolnFilters.orderStatus }
        : lincolnFilters),
      orderStatus: {
        ...lincolnFilters.orderStatus,
        infoText: tooltipBody(t("OrdersFilters.orderStatusInfoIntro"), [
          ["Accepted", t("OrdersFilters.orderStatusAcceptedInfo")],
          ["Created", t("OrdersFilters.orderStatusCreatedInfo")],
          ["Canceled", t("OrdersFilters.orderStatusCanceledInfo")],
        ]),
      },
    };
  }
  if (brand === "F" && market?.toUpperCase() === "CAN" && canadianPreorder) {
    return {
      actionRequired: activeFilters.actionRequired,
      orderStatus: activeFilters.orderStatus,
    };
  }
  return activeFilters;
};

export const initialFilters = {
  actionRequired: [],
  orderRequestType: [],
  vehicleSource: [],
  orderStatus: [],
};
